import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Clock, Leaf } from "lucide-react";

export default function Hero() {
  const handleRequestDemo = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    
    const endTime = new Date(tomorrow);
    endTime.setHours(11, 0, 0, 0);

    const formatDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d\d\d/g, "");
    };

    const start = formatDate(tomorrow);
    const end = formatDate(endTime);

    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=WorkRidePro+Demo&details=Demo+of+employee+transport+solutions&location=Online&dates=${start}/${end}`;
    
    window.open(url, '_blank');
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1635612418051-4a8727412644" 
          alt="Corporate Transport" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-transparent" />
      </div>

      <div className="container mx-auto px-6 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-8 animate-in slide-in-from-left-10 duration-700 fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white/90 text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
            Next Gen Employee Transport
          </div>
          
          <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight tracking-tight">
            The Smarter Way to <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-teal-200">Move Your Workforce</span>
          </h1>
          
          <p className="text-xl text-white/80 max-w-xl leading-relaxed">
            End-to-end managed employee transport services. We combine AI-driven routing, electric fleets, and 24/7 safety monitoring to deliver a seamless commute experience.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <Button 
              size="lg" 
              className="bg-secondary text-primary hover:bg-secondary/90 text-lg h-14 px-8"
              onClick={handleRequestDemo}
            >
              Request Demo <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg h-14 px-8" asChild>
              <Link to="/case-studies">View Case Studies</Link>
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-6 pt-12 border-t border-white/10">
            {[
              { icon: ShieldCheck, label: "100% Compliant" },
              { icon: Clock, label: "98% On-Time" },
              { icon: Leaf, label: "Zero Carbon Options" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 text-white/90">
                <item.icon className="h-6 w-6 text-secondary" />
                <span className="font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right side decorative element - maybe a floating card or just space for the image to show */}
        <div className="hidden lg:block relative h-[600px]">
           {/* Abstract shapes or floating UI elements could go here */}
           <div className="absolute top-10 right-10 bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 shadow-2xl max-w-xs animate-in fade-in zoom-in duration-1000 delay-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                   <ShieldCheck className="h-6 w-6 text-green-400" />
                </div>
                <div>
                   <p className="text-white font-bold">Safety First</p>
                   <p className="text-white/60 text-sm">Real-time monitoring active</p>
                </div>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                 <div className="h-full w-full bg-green-400 animate-pulse" />
              </div>
           </div>

           <div className="absolute bottom-20 left-0 bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 shadow-2xl max-w-xs animate-in fade-in zoom-in duration-1000 delay-500">
              <div className="flex items-center gap-4 mb-4">
                <div className="h-12 w-12 rounded-full bg-secondary/20 flex items-center justify-center">
                   <Clock className="h-6 w-6 text-secondary" />
                </div>
                <div>
                   <p className="text-white font-bold">On-Time Arrival</p>
                   <p className="text-white/60 text-sm">Route optimization enabled</p>
                </div>
              </div>
              <div className="flex justify-between text-xs text-white/60 mt-2">
                 <span>ETA: 08:45 AM</span>
                 <span className="text-secondary">On Track</span>
              </div>
           </div>
        </div>
      </div>
    </section>
  );
}
